/**
 * Element focus-visible mixin.
 * Adds "focus-visible" class and "focusVisible" reactive property.
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/:focus-visible
 * @template { import('@nrk/reactive-element').ReactiveElementBase } BaseClass
 * @param { BaseClass } Base
 */
export function FocusVisibleMixin(Base) {
  return class extends Base {
    static get reactiveProperties() {
      return {
        ...super.reactiveProperties,
        focusVisible: Boolean,
      };
    }

    /**
     * Constructor
     * @param  {...any} args
     */
    constructor(...args) {
      super();
      /** Flag indicating that the element has received focus using keyboard navigation */
      this.focusVisible = false;
      this._onKeydown = this._onKeydown.bind(this);
      this._onPointerdown = this._onPointerdown.bind(this);
    }

    connectedCallback() {
      document.addEventListener('keydown', this._onKeydown, true);
      super.connectedCallback && super.connectedCallback();
    }

    /**
     * Handle 'keydown' event
     * @param { KeyboardEvent } event
     */
    _onKeydown(event) {
      // Enable when TAB
      if (event.which === 9 && !this.focusVisible) {
        this.focusVisible = true;
        this.classList.add('focus-visible');
        document.addEventListener('mousedown', this._onPointerdown, true);
        document.addEventListener('pointerdown', this._onPointerdown, true);
        document.addEventListener('touchstart', this._onPointerdown, true);
      }
    }

    /**
     * Handle mouse/pointer/touch event
     * @param { MouseEvent | PointerEvent | TouchEvent } event
     */
    _onPointerdown(event) {
      this.focusVisible = false;
      this.classList.remove('focus-visible');
      this._removePointerHandlers();
    }

    _removePointerHandlers() {
      document.removeEventListener('mousedown', this._onPointerdown, true);
      document.removeEventListener('pointerdown', this._onPointerdown, true);
      document.removeEventListener('touchstart', this._onPointerdown, true);
    }

    disconnectedCallback() {
      this._removePointerHandlers();
      document.removeEventListener('keydown', this._onKeydown, true);
      super.disconnectedCallback && super.disconnectedCallback();
    }
  };
}
